import { jsPDF } from "jspdf";
import { formatTriageDetected, TRIAGE_BUCKET_LABEL } from "./careTriageModel";

/**
 * sections: [{ id, label, rows }] — exported in order, each under its own header.
 */

const COLUMNS = [
  { header: "Client", value: (r) => r.clientName },
  { header: "Client ID", value: (r) => r.clientShortId },
  { header: "Triage", value: (r) => TRIAGE_BUCKET_LABEL[r.bucket] || r.bucket },
  { header: "AiP Previous", value: (r) => r.aipPrevious },
  { header: "AiP Current", value: (r) => r.aipCurrent },
  { header: "Change", value: (r) => r.change },
  { header: "What Changed", value: (r) => r.note },
  { header: "Source", value: (r) => r.source },
  { header: "Detected", value: (r) => formatTriageDetected(r.changeDate) },
  { header: "Status", value: (r) => r.workflowStatus },
  { header: "Review Status", value: (r) => r.reviewStatusLabel || r.reviewResult },
  { header: "Owner", value: (r) => r.owner },
  { header: "Due", value: (r) => (r.dueDate ? formatTriageDetected(r.dueDate) : "") },
  { header: "Priority", value: (r) => r.priority },
  { header: "Notes", value: (r) => r.notes },
  { header: "Outcome", value: (r) => r.outcome },
  { header: "Resolved At", value: (r) => (r.resolvedAt ? formatTriageDetected(r.resolvedAt) : "") },
  { header: "Resolved By", value: (r) => r.resolvedBy },
];

function sectionTitle(section) {
  return `${section.label} (${section.rows.length})`;
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(value) {
  const s = String(value ?? "").replace(/"/g, '""');
  return `"${s}"`;
}

export function exportTriageCasesCsv(sections, filename = "triage-queue.csv") {
  const lines = [];
  sections.forEach((section, i) => {
    if (i > 0) lines.push("");
    lines.push(csvEscape(sectionTitle(section)));
    lines.push(COLUMNS.map((c) => csvEscape(c.header)).join(","));
    if (!section.rows.length) {
      lines.push(csvEscape("No cases"));
      return;
    }
    section.rows.forEach((r) => {
      lines.push(COLUMNS.map((c) => csvEscape(c.value(r))).join(","));
    });
  });

  download(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }), filename);
}

const dash = (v) => (v == null || v === "" ? "—" : String(v));

const PDF_ACTIVE_COLUMNS = [
  { header: "Client", width: 1.5, value: (r) => `${dash(r.clientName)}\n#${dash(r.clientShortId)}` },
  {
    header: "AIP Score",
    width: 0.8,
    value: (r) => `${dash(r.aipPrevious)} -> ${dash(r.aipCurrent)}${r.change != null ? ` (${r.change > 0 ? "+" : ""}${r.change})` : ""}`,
  },
  { header: "What Changed", width: 1.7, value: (r) => dash(r.note) },
  { header: "Source", width: 1, value: (r) => dash(r.source) },
  { header: "Detected", width: 1, value: (r) => dash(formatTriageDetected(r.changeDate)) },
  { header: "Status", width: 0.8, value: (r) => dash(r.workflowStatus).replace(/_/g, " ") },
  { header: "Review Status", width: 1, value: (r) => dash(r.reviewStatusLabel) },
  { header: "Owner", width: 0.9, value: (r) => dash(r.owner) },
  { header: "Due", width: 1, value: (r) => (r.dueDate ? formatTriageDetected(r.dueDate) : "—") },
  { header: "Priority", width: 0.6, value: (r) => dash(r.priority) },
  { header: "Notes", width: 1.4, value: (r) => dash(r.notes) },
];

const PDF_RESOLVED_COLUMNS = [
  ...PDF_ACTIVE_COLUMNS.slice(0, 5),
  { header: "Final Result", width: 1.1, value: (r) => dash(r.reviewResult || r.reviewStatusLabel || "Approved and Closed") },
  { header: "Owner", width: 0.9, value: (r) => dash(r.owner) },
  { header: "Priority", width: 0.6, value: (r) => dash(r.priority) },
  { header: "Resolved At", width: 1, value: (r) => (r.resolvedAt ? formatTriageDetected(r.resolvedAt) : "—") },
  { header: "Resolved By", width: 0.9, value: (r) => dash(r.resolvedBy) },
  { header: "Outcome", width: 1.3, value: (r) => dash(r.outcome) },
];

export function exportTriageCasesPdf(sections, { title = "Triage Cases", subtitle = "", filename = "triage-queue.pdf" } = {}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 32;
  const tableW = pageW - margin * 2;
  const pad = 4;
  const lineH = 10;
  let y = margin;

  const ensureSpace = (h) => {
    if (y + h <= pageH - margin) return false;
    doc.addPage();
    y = margin;
    return true;
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(17, 24, 39);
  doc.text(title, margin, y + 12);
  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(`${subtitle ? `${subtitle} · ` : ""}Exported ${new Date().toLocaleString()}`, margin, y + 6);
  y += 20;

  const drawHeaderRow = (cols, widths) => {
    doc.setFillColor(238, 244, 255);
    doc.rect(margin, y, tableW, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(30, 58, 138);
    let x = margin;
    cols.forEach((c, i) => {
      doc.text(c.header, x + pad, y + 12);
      x += widths[i];
    });
    y += 18;
  };

  sections.forEach((section) => {
    const cols = section.id === "resolved" ? PDF_RESOLVED_COLUMNS : PDF_ACTIVE_COLUMNS;
    const totalWeight = cols.reduce((s, c) => s + c.width, 0);
    const widths = cols.map((c) => (c.width / totalWeight) * tableW);

    ensureSpace(22 + 18 + 24);
    doc.setFillColor(243, 244, 246);
    doc.rect(margin, y, tableW, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(55, 65, 81);
    doc.text(sectionTitle(section), margin + pad + 2, y + 15);
    y += 22;

    if (!section.rows.length) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(156, 163, 175);
      doc.text("No cases", margin + pad + 2, y + 14);
      y += 28;
      return;
    }

    drawHeaderRow(cols, widths);

    section.rows.forEach((r) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const cells = cols.map((c, i) => doc.splitTextToSize(String(c.value(r)), widths[i] - pad * 2));
      const rowH = Math.max(...cells.map((lines) => lines.length)) * lineH + pad * 2;
      if (ensureSpace(rowH)) drawHeaderRow(cols, widths);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(31, 41, 55);
      let x = margin;
      cells.forEach((lines, i) => {
        doc.text(lines, x + pad, y + pad + 7);
        x += widths[i];
      });
      y += rowH;
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, margin + tableW, y);
    });
    y += 16;
  });

  doc.save(filename);
}
